import React from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { RATING, fmt, fmtPct, fmtCap, scoreColor, flowColor } from './constants';

const RankingsTab = ({ items, rankings, expandedIcb, toggleLeaders }) => {
  const { t } = useTranslation();

  if (!items.length) {
    return <Alert severity="info">{t('icb.common.noRankingData')}</Alert>;
  }

  return (
    <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 2, border: '1px solid #e2e8f0' }}>
      <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {t('icb.rankings.title', { count: items.length, level: rankings?.level })}
          </Typography>
          <Chip
            icon={<HelpOutline sx={{ fontSize: 14, color: '#6366f1 !important' }} />}
            label={t('icb.rankings.clickHint')}
            size="small"
            sx={{ bgcolor: '#eef2ff', color: '#4f46e5', fontSize: '0.7rem', fontWeight: 600, height: 22, '& .MuiChip-icon': { ml: 0.5 } }}
          />
        </Box>
        <Chip label={t('icb.rankings.dateChip', { date: rankings?.trade_date || '-' })} size="small" color="primary" variant="outlined" />
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                { k: 'rank', a: 'left' }, { k: 'industry', a: 'left' }, { k: 'rating', a: 'left' },
                { k: 'score', a: 'right' }, { k: 'rs', a: 'right' }, { k: 'flow', a: 'right' },
                { k: 'breadth', a: 'right' }, { k: 'momentum', a: 'right' }, { k: 'stockCount', a: 'right' },
                { k: 'marketCap', a: 'right' }, { k: 'change1D', a: 'right' }, { k: 'change5D', a: 'right' },
                { k: 'change20D', a: 'right' }, { k: 'topGainer', a: 'left' }, { k: 'topLoser', a: 'left' },
              ].map(({ k, a }) => (
                <TableCell key={k} align={a}
                  sx={{ fontWeight: 700, bgcolor: '#f8f9fa', fontSize: '0.75rem', whiteSpace: 'nowrap', py: 1 }}>
                  {t(`icb.rankings.headers.${k}`)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((r) => {
              const rc = RATING[r.rating] || RATING.NEUTRAL;
              const isOpen = expandedIcb === r.icb_code;
              return (
                <TableRow
                  key={r.icb_code}
                  hover
                  onClick={() => toggleLeaders(r.icb_code)}
                  sx={{
                    cursor: 'pointer',
                    '&:nth-of-type(odd)': { bgcolor: isOpen ? '#dbeafe' : '#fafafa' },
                    bgcolor: isOpen ? '#dbeafe !important' : undefined,
                    borderLeft: isOpen ? '3px solid #6366f1' : '3px solid transparent',
                    transition: 'background-color 0.15s, border-color 0.15s',
                    '&:hover': { bgcolor: isOpen ? '#bfdbfe !important' : '#eef2ff !important' },
                  }}
                >
                  <TableCell><Typography fontWeight={700} fontSize="0.8rem" color="#94a3b8">#{r.rank}</Typography></TableCell>
                  <TableCell>
                    <Typography fontWeight={600} fontSize="0.85rem">{r.name_vi || r.name_en}</Typography>
                    <Typography variant="caption" color="text.secondary" fontSize="0.7rem">{r.name_en}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={t(`icb.rating.${r.rating || 'NEUTRAL'}`)} size="small"
                      sx={{ bgcolor: rc.bg, color: rc.color, fontWeight: 700, fontSize: '0.6rem', height: 22 }} />
                  </TableCell>
                  <TableCell align="right">
                    <Chip label={fmt(r.final_score)} size="small"
                      sx={{ bgcolor: scoreColor(r.final_score), color: 'white', fontWeight: 700, minWidth: 42, height: 22 }} />
                  </TableCell>
                  <TableCell align="right">{fmt(r.rs_score)}</TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={600} fontSize="0.8rem" color={flowColor(r.foreign_flow_score)}>{fmt(r.foreign_flow_score)}</Typography>
                  </TableCell>
                  <TableCell align="right">{fmt(r.breadth_score)}</TableCell>
                  <TableCell align="right">{fmt(r.momentum_score)}</TableCell>
                  <TableCell align="right">
                    <Chip label={r.stock_count} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{fmtCap(r.total_market_cap)}</TableCell>
                  <TableCell align="right">
                    <Typography fontSize="0.8rem" color={r.avg_change_1d >= 0 ? '#10b981' : '#ef4444'}>{fmtPct(r.avg_change_1d)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontSize="0.8rem" color={r.avg_change_5d >= 0 ? '#10b981' : '#ef4444'}>{fmtPct(r.avg_change_5d)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontSize="0.8rem" color={r.avg_change_20d >= 0 ? '#10b981' : '#ef4444'}>{fmtPct(r.avg_change_20d)}</Typography>
                  </TableCell>
                  <TableCell>
                    {r.top_gainer && (
                      <Chip label={r.top_gainer} size="small"
                        sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {r.top_loser && (
                      <Chip label={r.top_loser} size="small"
                        sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

RankingsTab.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    icb_code: PropTypes.string,
    rank: PropTypes.number,
    name_vi: PropTypes.string,
    name_en: PropTypes.string,
    rating: PropTypes.string,
    final_score: PropTypes.number,
    rs_score: PropTypes.number,
    foreign_flow_score: PropTypes.number,
    breadth_score: PropTypes.number,
    momentum_score: PropTypes.number,
    stock_count: PropTypes.number,
    total_market_cap: PropTypes.number,
    avg_change_1d: PropTypes.number,
    avg_change_5d: PropTypes.number,
    avg_change_20d: PropTypes.number,
    top_gainer: PropTypes.string,
    top_loser: PropTypes.string,
  })).isRequired,
  rankings: PropTypes.shape({
    level: PropTypes.string,
    trade_date: PropTypes.string,
  }),
  expandedIcb: PropTypes.string,
  toggleLeaders: PropTypes.func.isRequired,
};

export default RankingsTab;
